import * as XLSX from 'xlsx';

export interface UniversalRecord {
  [key: string]: string | number;
}

const toProperCase = (text: string): string => {
  if (!text) return '';
  
  // Split into words while preserving order
  const words = text.toLowerCase().split(' ');
  
  return words.map(word => {
    // Handle special cases
    if (word.length === 0) return word;
    if (['ii', 'iii', 'iv'].includes(word.toLowerCase())) return word.toUpperCase();
    if (['llc', 'inc'].includes(word.toLowerCase())) return word.toUpperCase();
    
    // Keep numbers as-is
    if (/^\d+$/.test(word)) return word;
    
    // Capitalize first letter only
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
};

const cleanName = (name: string): { firstName: string; lastName: string } => {
  if (!name || name.trim() === '') return { firstName: '', lastName: '' };
  
  const cleaned = name.trim()
    .replace(/\s+/g, ' ')
    .replace(/,?\s*(JR|SR|II|III|IV|ESQ|PHD|MD)\.?$/i, '')
    .trim();
  
  // Check if it looks like a company (LLC, INC, CORP, etc.)
  if (/\b(LLC|INC|CORP|COMPANY|CO\b|TRUST|BANK|CREDIT|UNION)/i.test(cleaned)) {
    return { firstName: '', lastName: toProperCase(cleaned) };
  }
  
  const parts = cleaned.split(/\s+/);
  
  if (parts.length === 1) {
    return { firstName: '', lastName: toProperCase(parts[0]) };
  }
  
  // County records are typically: LAST_NAME FIRST_NAME MIDDLE_INITIAL
  // So first word is last name, rest is first name (excluding single letter middle initials)
  const lastName = toProperCase(parts[0]);
  const remainingParts = parts.slice(1);
  
  // Remove single letter middle initials from the end
  const firstNameParts = remainingParts.filter((part, idx) => {
    // Keep all parts except single letters at the end
    if (part.length === 1) {
      return idx === 0; // Only keep single letter if it's the only thing left
    }
    return true;
  });
  
  const firstName = firstNameParts.length > 0 
    ? toProperCase(firstNameParts.join(' ')) 
    : toProperCase(remainingParts[0] || '');
  
  return { firstName, lastName };
};

const parseAddress = (address: string): { street: string; city: string; state: string; zip: string } => {
  if (!address || address.trim() === '') {
    return { street: '', city: '', state: '', zip: '' };
  }
  
  const cleaned = address.trim().replace(/\s+/g, ' ');
  const parts = cleaned.split(',').map(p => p.trim());
  
  if (parts.length < 2) {
    return { street: toProperCase(cleaned), city: '', state: '', zip: '' };
  }
  
  // Part 1: Street address (number + street name)
  const street = toProperCase(parts[0]);
  
  // Part 2: City
  const city = parts.length > 1 ? toProperCase(parts[1]) : '';
  
  // Part 3: State and ZIP (could be "Texas 78070" or "TX 78070" or just "TX")
  let state = '';
  let zip = '';
  
  if (parts.length > 2) {
    const lastPart = parts[parts.length - 1].trim();
    
    // Try to extract ZIP code (5 digits or 5+4 format)
    const zipMatch = lastPart.match(/\b(\d{5}(?:-\d{4})?)\b/);
    if (zipMatch) {
      zip = zipMatch[1];
      // Everything before the ZIP is the state
      state = lastPart.replace(zipMatch[0], '').trim().toUpperCase();
      
      // Convert full state names to abbreviations if needed
      if (state.length > 2) {
        const stateAbbrev = STATE_ABBREVIATIONS[state];
        if (stateAbbrev) {
          state = stateAbbrev;
        }
      }
    } else {
      // No ZIP found, the whole thing is the state
      state = lastPart.toUpperCase();
      if (state.length > 2) {
        const stateAbbrev = STATE_ABBREVIATIONS[state];
        if (stateAbbrev) {
          state = stateAbbrev;
        }
      }
    }
  }
  
  return { street, city, state, zip };
};

const STATE_ABBREVIATIONS: { [key: string]: string } = {
  'ALABAMA': 'AL', 'ALASKA': 'AK', 'ARIZONA': 'AZ', 'ARKANSAS': 'AR', 'CALIFORNIA': 'CA',
  'COLORADO': 'CO', 'CONNECTICUT': 'CT', 'DELAWARE': 'DE', 'FLORIDA': 'FL', 'GEORGIA': 'GA',
  'HAWAII': 'HI', 'IDAHO': 'ID', 'ILLINOIS': 'IL', 'INDIANA': 'IN', 'IOWA': 'IA',
  'KANSAS': 'KS', 'KENTUCKY': 'KY', 'LOUISIANA': 'LA', 'MAINE': 'ME', 'MARYLAND': 'MD',
  'MASSACHUSETTS': 'MA', 'MICHIGAN': 'MI', 'MINNESOTA': 'MN', 'MISSISSIPPI': 'MS', 'MISSOURI': 'MO',
  'MONTANA': 'MT', 'NEBRASKA': 'NE', 'NEVADA': 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', 'OHIO': 'OH',
  'OKLAHOMA': 'OK', 'OREGON': 'OR', 'PENNSYLVANIA': 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD', 'TENNESSEE': 'TN', 'TEXAS': 'TX', 'UTAH': 'UT', 'VERMONT': 'VT',
  'VIRGINIA': 'VA', 'WASHINGTON': 'WA', 'WEST VIRGINIA': 'WV', 'WISCONSIN': 'WI', 'WYOMING': 'WY'
};

export interface ColumnMapping {
  columnName: string;
  type: 'name' | 'address' | 'other';
}

export const autoDetectColumnMappings = (headers: string[]): ColumnMapping[] => {
  return headers.map(header => {
    const headerLower = header.toLowerCase();
    
    let type: 'name' | 'address' | 'other' = 'other';
    
    if (headerLower.includes('address')) {
      type = 'address';
    } else if (
      headerLower.includes('name') ||
      headerLower.includes('seller') ||
      headerLower.includes('buyer') ||
      headerLower.includes('grantor') ||
      headerLower.includes('grantee')
    ) {
      type = 'name';
    }
    
    return {
      columnName: header,
      type,
    };
  });
};

export const processUniversalCountyData = (
  rawData: string,
  columnMappings?: ColumnMapping[]
): UniversalRecord[] => {
  const lines = rawData.split('\n').filter(line => line.trim());
  
  if (lines.length === 0) return [];
  
  // Get headers from first line
  const headers = lines[0].split('\t').map(h => h.trim());
  
  // Use provided mappings or auto-detect
  const mappings = columnMappings || autoDetectColumnMappings(headers);
  
  const records: UniversalRecord[] = [];
  
  // Process data lines
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t');
    const record: UniversalRecord = {};
    
    headers.forEach((header, idx) => {
      const value = values[idx]?.trim() || '';
      const mapping = mappings.find(m => m.columnName === header);
      const columnType = mapping?.type || 'other';
      
      if (columnType === 'address' && value) {
        const { street, city, state, zip } = parseAddress(value);
        // Create clean column names by removing "Address" from the header
        const baseColumnName = header.replace(/address/i, '').trim() || '';
        const prefix = baseColumnName ? `${baseColumnName} ` : '';
        
        if (street) record[`${prefix}Street`] = street;
        if (city) record[`${prefix}City`] = city;
        if (state) record[`${prefix}State`] = state;
        if (zip) record[`${prefix}ZIP`] = zip;
        // Don't add the original unparsed address column
      } else if (columnType === 'name' && value) {
        const { firstName, lastName } = cleanName(value);
        // Create clean column names
        const baseColumnName = header.replace(/name/i, '').trim() || header;
        if (firstName && lastName) {
          // Both names: create "FirstName LastName" format in single column
          record[baseColumnName] = `${firstName} ${lastName}`;
        } else if (lastName) {
          // Only last name (likely a company)
          record[baseColumnName] = lastName;
        } else if (firstName) {
          // Only first name (edge case)
          record[baseColumnName] = firstName;
        }
        // Don't add the original all-caps column
      } else {
        // Keep as-is
        record[header] = value;
      }
    });
    
    records.push(record);
  }
  
  return records;
};

export const exportUniversalToExcel = (records: UniversalRecord[], filename: string = 'county_data_export.xlsx'): void => {
  if (records.length === 0) return;
  
  const worksheet = XLSX.utils.json_to_sheet(records);
  
  // Auto-size columns
  const columnWidths = Object.keys(records[0]).map(key => {
    const maxLength = Math.max(
      key.length,
      ...records.map(r => String(r[key] || '').length)
    );
    return { wch: Math.min(maxLength + 2, 50) };
  });
  
  worksheet['!cols'] = columnWidths;
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'County Data');
  XLSX.writeFile(workbook, filename);
};
